import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, ArrowRight, ArrowLeft, Loader2, Mic, MicOff, Copy, Check, FileText, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import paymentService from '../services/paymentService';
import { generateClaudeCodePrompt } from '../services/claudePromptService';
import { downloadFormats, extractAppName } from '../services/downloadService';
import { projectService } from '../services/projectService';
import { trackEvent } from '../services/analytics';
import PaymentModal from './PaymentModal';
import Auth from './Auth';

// ─── Loading tips shown while generating ─────────────────────
const LOADING_TIPS = [
  'Analyzing your app concept...',
  'Defining core functionalities...',
  'Mapping out user flows...',
  'Designing page layouts...',
  'Specifying shadcn/ui components...',
  'Writing acceptance criteria...',
  'Building project init commands...',
  'Structuring file architecture...',
  'Adding loading & error states...',
  'Running quality checks...',
  'Finalizing your one-shot prompt...',
];

const ClaudeCodeLanding = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isPaymentRequired, initiatePayment, refreshPaymentStatus } = usePayment();

  // ─── State ──────────────────────────────────────────────────
  const [appIdea, setAppIdea] = useState('');
  const [generatedPRD, setGeneratedPRD] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [view, setView] = useState('input'); // 'input' | 'generating' | 'result'
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadingTipIndex, setLoadingTipIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Voice input
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const accumulatedTranscriptRef = useRef('');
  const initialIdeaRef = useRef('');
  const promptRef = useRef(null);

  const BUFFER_KEY = 'claude_landing_buffer';

  // ─── Loading tip rotation ──────────────────────────────────
  useEffect(() => {
    if (view !== 'generating') return;
    const tipInterval = setInterval(() => {
      setLoadingTipIndex(prev => (prev + 1) % LOADING_TIPS.length);
    }, 3500);
    const timerInterval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => { clearInterval(tipInterval); clearInterval(timerInterval); };
  }, [view]);

  // ─── Voice Input ───────────────────────────────────────────
  const startSpeechToText = () => {
    if (isRecording) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition is not supported in this browser.'); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;
    initialIdeaRef.current = appIdea.trim();
    accumulatedTranscriptRef.current = '';
    recognition.onresult = (event) => {
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          const raw = (res[0]?.transcript || '').trim();
          const cleaned = raw.replace(/\b(\w+)(?:\s+\1\b)+/gi, '$1');
          finalChunk += (finalChunk ? ' ' : '') + cleaned;
        }
      }
      if (finalChunk) {
        accumulatedTranscriptRef.current = [accumulatedTranscriptRef.current, finalChunk].filter(Boolean).join(' ');
        const base = initialIdeaRef.current ? initialIdeaRef.current + ' ' : '';
        setAppIdea((base + accumulatedTranscriptRef.current).trim());
      }
    };
    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };
  const stopSpeechToText = () => { if (!isRecording) return; recognitionRef.current?.stop(); setIsRecording(false); };

  // ─── Restore buffered state ────────────────────────────────
  useEffect(() => {
    try {
      const stored = localStorage.getItem(BUFFER_KEY);
      if (stored) {
        const { appIdea: savedIdea, generatedPRD: savedPRD } = JSON.parse(stored);
        if (savedIdea) setAppIdea(savedIdea);
        if (savedPRD) { setGeneratedPRD(savedPRD); setView('result'); }
        localStorage.removeItem(BUFFER_KEY);
      }
    } catch {}
  }, []);

  // After signup → trigger Stripe with the SAME $4.99 plan the original valycode uses
  useEffect(() => {
    try {
      const shouldCharge = localStorage.getItem('claude_landing_autocharge') === '1';
      if (isAuthenticated && shouldCharge) {
        localStorage.removeItem('claude_landing_autocharge');
        setShowAuthModal(false);
        // Flag so HomePage redirects back to /claude-code after Stripe return
        localStorage.setItem('claude_landing_return', '1');
        (async () => { try { await initiatePayment('prd-starter'); } catch { setShowPaymentModal(true); } })();
      }
    } catch {}
  }, [isAuthenticated, initiatePayment]);

  // After payment success + buffered idea restored → auto-generate
  useEffect(() => {
    const shouldAutoGen = localStorage.getItem('claude_landing_autogenerate') === '1';
    if (!shouldAutoGen || !user?.id || !appIdea || generatedPRD || view === 'generating') return;

    // Fresh Supabase check — don't trust potentially-stale React state
    (async () => {
      try {
        const isPro = await paymentService.hasUserPaid();
        if (isPro) {
          localStorage.removeItem('claude_landing_autogenerate');
          runGeneration();
        }
      } catch (err) {
        console.warn('Pro status check failed:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPaymentRequired, appIdea, generatedPRD]);

  // ─── Generate ──────────────────────────────────────────────
  const runGeneration = async () => {
    trackEvent('Claude Landing PRD Generate', { source: 'claude_landing' });
    setIsGenerating(true);
    setView('generating');
    setLoadingTipIndex(0);
    setElapsedSeconds(0);

    try {
      // Stream tokens live into state so the preview fills as the model generates.
      // Flip to the result view on first token so users see streaming text immediately.
      let firstToken = true;
      const prd = await generateClaudeCodePrompt(appIdea, (partial) => {
        setGeneratedPRD(partial);
        if (firstToken) {
          firstToken = false;
          setView('result');
        }
      });
      setGeneratedPRD(prd);
      setView('result');
    } catch (error) {
      console.error('PRD generation failed:', error);
      setGeneratedPRD(`# Claude Code Prompt\n\n## App Idea\n${appIdea}\n\n## Error\nGeneration encountered an error. Please try again.`);
      setView('result');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appIdea.trim()) return;

    // Not signed in → buffer idea + trigger signup flow (Stripe auto-starts after signup)
    if (!user || !user.id) {
      bufferState();
      localStorage.setItem('claude_landing_autocharge', '1');
      localStorage.setItem('claude_landing_autogenerate', '1');
      setShowPaymentModal(false);
      setShowAuthModal(true);
      return;
    }

    // Signed in → do a FRESH Supabase check for pro status
    // (React state from PaymentContext may still be loading on first render)
    setIsGenerating(true);
    let isPro = false;
    try {
      isPro = await paymentService.hasUserPaid();
      // Sync the React state so other parts of the app see the correct value
      if (refreshPaymentStatus) refreshPaymentStatus();
    } catch (err) {
      console.warn('Pro status check failed, falling back to React state:', err);
      isPro = !isPaymentRequired;
    }
    setIsGenerating(false);

    if (!isPro) {
      // Confirmed not-pro → show payment modal
      bufferState();
      localStorage.setItem('claude_landing_autogenerate', '1');
      localStorage.setItem('claude_landing_return', '1');
      setShowPaymentModal(true);
      return;
    }

    // Pro user → run generation immediately
    runGeneration();
  };

  // ─── Copy to clipboard ────────────────────────────────────
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedPRD);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = generatedPRD;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // ─── Download as .txt ─────────────────────────────────────
  const bufferState = () => {
    try { localStorage.setItem(BUFFER_KEY, JSON.stringify({ appIdea, generatedPRD, ts: Date.now() })); } catch {}
  };

  const handleDownloadTxt = async () => {
    if (!generatedPRD) return;
    setIsDownloading(true);
    try {
      const appName = extractAppName(generatedPRD) || extractAppName(appIdea) || 'My App';
      // Download as text
      const blob = new Blob([generatedPRD], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${appName.replace(/\s+/g, '-')}-claude-prompt.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Save to project in background
      if (isAuthenticated && user?.id) {
        savePRDToProject(appName).catch(() => {});
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const savePRDToProject = async (appName) => {
    try {
      const projectData = {
        user_id: user.id,
        name: appName,
        description: appIdea.length > 200 ? appIdea.substring(0, 200) + '...' : appIdea,
        type: 'Web App', project_type: 'Web App',
        features: ['User Interface', 'Core Functionality', 'Data Management'],
        metadata: { app_idea: appIdea, created_via: 'claude_landing_page', is_landing_page_project: true }
      };
      const created = await projectService.createProject(user.id, projectData);
      if (!created?.id) return;
      await projectService.saveGeneratedFile(created.id, {
        fileName: `${appName.replace(/\s+/g, '-')}-claude-prd.md`,
        fileType: 'PRD Document', content: generatedPRD, prdSource: 'claude_landing',
        metadata: { icon: 'FileText', generated_date: new Date().toISOString().split('T')[0], creation_source: 'claude_landing_page' }
      });
    } catch {}
  };

  // ─── Reset ─────────────────────────────────────────────────
  const handleReset = () => {
    setAppIdea('');
    setGeneratedPRD('');
    setView('input');
    setCopied(false);
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-claude-bg">

      {/* ════════════════════ HEADER ════════════════════ */}
      <header className="sticky top-0 z-40 bg-claude-bg/95 backdrop-blur-sm border-b border-claude-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/aitoolslogos/claude-ai.png" alt="Claude" className="h-7 w-7 rounded-sm" />
            <span className="font-claude-serif text-lg text-claude-text font-semibold tracking-tight">
              Valycode <span className="text-claude-muted-fg font-normal">for Claude Code</span>
            </span>
          </div>
          <button
            onClick={() => view === 'input' ? navigate('/') : handleReset()}
            className="flex items-center gap-1.5 text-sm text-claude-muted-fg hover:text-claude-text transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {view === 'input' ? 'Back to Valycode' : 'New prompt'}
          </button>
        </div>
      </header>

      <AnimatePresence mode="wait">

        {/* ════════════════════ INPUT VIEW ════════════════════ */}
        {view === 'input' && (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <section className="relative py-16 sm:py-20 px-4">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="font-claude-serif text-4xl sm:text-5xl lg:text-6xl text-claude-text leading-[1.15] tracking-tight mb-6 sm:mb-8">
                  <span className="block">One-shot prompt builder for Claude Code.</span>
                  <span className="block mt-3 sm:mt-4 text-claude-primary">From idea to app</span>
                </h1>

                <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
                  <div className="relative">
                    <div className="flex items-center justify-end mb-2">
                      <button
                        type="button"
                        onClick={isRecording ? stopSpeechToText : startSpeechToText}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          isRecording ? 'border-red-400 text-red-600 bg-red-50' : 'border-claude-border text-claude-muted-fg hover:text-claude-text hover:border-claude-text/30'
                        }`}
                      >
                        {isRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                        {isRecording ? 'Stop' : 'Speak your idea'}
                      </button>
                    </div>
                    <textarea
                      value={appIdea}
                      onChange={(e) => setAppIdea(e.target.value)}
                      placeholder="Describe your app idea... e.g. 'A personal finance tracker that categorizes expenses, shows spending trends, and sends budget alerts'"
                      className="w-full h-44 p-5 rounded-xl resize-none text-claude-text placeholder-claude-muted-fg/60 text-base leading-relaxed bg-white border border-claude-border focus:border-claude-primary focus:ring-2 focus:ring-claude-primary/20 transition-all outline-none"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={!appIdea.trim()}
                    whileHover={{ scale: appIdea.trim() ? 1.01 : 1 }}
                    whileTap={{ scale: appIdea.trim() ? 0.99 : 1 }}
                    className={`w-full mt-4 py-4 px-8 rounded-xl font-medium text-base transition-all flex items-center justify-center gap-2 ${
                      appIdea.trim() ? 'bg-claude-primary text-white hover:bg-claude-primary/90 shadow-sm' : 'bg-claude-accent text-claude-muted-fg cursor-not-allowed'
                    }`}
                  >
                    Generate Claude Code Prompt
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </form>
              </div>
            </section>

            {/* How It Works */}
            <section className="py-20 px-4 bg-claude-bg">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-14">
                  <h2 className="font-claude-serif text-3xl sm:text-4xl text-claude-text tracking-tight">How it works</h2>
                  <p className="text-base text-claude-muted-fg mt-3">Three steps to a production-ready app</p>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { num: '01', title: 'Describe your idea', desc: 'Type or speak your app concept in plain language. No technical jargon needed.' },
                    { num: '02', title: 'Get your optimized prompt', desc: 'We generate a comprehensive, Claude Code-ready PRD with init commands, file structure, and build sequence.' },
                    { num: '03', title: 'Paste and build', desc: 'Drop the prompt into Claude Code. It scaffolds, codes, and ships your entire MVP in one session.' }
                  ].map((step, i) => (
                    <div key={step.num} className="bg-white border border-claude-border rounded-2xl p-7 hover:border-claude-primary/30 transition-colors">
                      <span className="text-xs font-semibold text-claude-primary tracking-widest">{step.num}</span>
                      <h3 className="font-claude-serif text-xl text-claude-text mt-3 mb-2">{step.title}</h3>
                      <p className="text-sm text-claude-muted-fg leading-relaxed">{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 px-4 bg-white">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="font-claude-serif text-3xl sm:text-4xl md:text-5xl text-claude-text tracking-tight mb-5">Ready to build with Claude Code?</h2>
                <p className="text-lg text-claude-muted-fg mb-8 max-w-xl mx-auto leading-relaxed">Stop guessing what to type. Get a prompt that Claude Code actually understands.</p>
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="bg-claude-primary text-white px-10 py-4 rounded-xl font-medium text-base hover:bg-claude-primary/90 transition-all shadow-sm">
                  Generate your first prompt
                </button>
              </div>
            </section>

            <footer className="border-t border-claude-border bg-claude-bg">
              <div className="max-w-5xl mx-auto px-6 py-8 flex items-center justify-between">
                <span className="text-sm text-claude-muted-fg">Powered by <button onClick={() => navigate('/')} className="text-claude-text hover:text-claude-primary transition-colors underline">Valycode</button></span>
                <span className="text-sm text-claude-muted-fg"><a href="mailto:m.sayed@valyfy.com" className="hover:text-claude-text transition-colors">Contact</a></span>
              </div>
            </footer>
          </motion.div>
        )}

        {/* ════════════════════ GENERATING VIEW ════════════════════ */}
        {view === 'generating' && (
          <motion.div
            key="generating"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="max-w-4xl mx-auto px-6 py-12"
          >
            {/* App idea recap */}
            <div className="mb-8">
              <p className="text-sm text-claude-muted-fg mb-1">Generating prompt for</p>
              <p className="text-base text-claude-text font-medium leading-relaxed">{appIdea}</p>
            </div>

            {/* Preview container */}
            <div className="bg-white border border-claude-border rounded-2xl overflow-hidden">
              {/* Tab bar */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-claude-border bg-claude-bg/50">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-claude-primary" />
                  <span className="text-sm font-medium text-claude-text">Claude Code Prompt</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-claude-muted-fg tabular-nums">{Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}</span>
                  <Loader2 className="w-4 h-4 text-claude-primary animate-spin" />
                </div>
              </div>

              {/* Skeleton content */}
              <div className="p-6 space-y-6 min-h-[400px]">
                {/* Progress indicator */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="relative w-full h-1 bg-claude-accent rounded-full overflow-hidden">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-claude-primary rounded-full"
                      initial={{ width: '0%' }}
                      animate={{ width: '85%' }}
                      transition={{ duration: 60, ease: 'linear' }}
                    />
                  </div>
                </div>

                {/* Current step */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={loadingTipIndex}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-3 py-3 px-4 bg-claude-primary/5 border border-claude-primary/10 rounded-lg"
                  >
                    <Loader2 className="w-4 h-4 text-claude-primary animate-spin flex-shrink-0" />
                    <span className="text-sm text-claude-text font-medium">{LOADING_TIPS[loadingTipIndex]}</span>
                  </motion.div>
                </AnimatePresence>

                {/* Skeleton lines */}
                <div className="space-y-4 mt-6">
                  {/* Section 1 skeleton */}
                  <div>
                    <div className="h-5 w-48 bg-claude-accent rounded animate-pulse mb-3" />
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-5/6 bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-4/6 bg-claude-accent/60 rounded animate-pulse" />
                    </div>
                  </div>
                  {/* Section 2 skeleton */}
                  <div className="pt-3">
                    <div className="h-5 w-40 bg-claude-accent rounded animate-pulse mb-3" />
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-3/4 bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-5/6 bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-2/3 bg-claude-accent/60 rounded animate-pulse" />
                    </div>
                  </div>
                  {/* Section 3 skeleton */}
                  <div className="pt-3">
                    <div className="h-5 w-52 bg-claude-accent rounded animate-pulse mb-3" />
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-4/5 bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-3/5 bg-claude-accent/60 rounded animate-pulse" />
                    </div>
                  </div>
                  {/* Section 4 skeleton */}
                  <div className="pt-3">
                    <div className="h-5 w-56 bg-claude-accent rounded animate-pulse mb-3" />
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-claude-accent/60 rounded animate-pulse" />
                      <div className="h-3 w-5/6 bg-claude-accent/60 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ════════════════════ RESULT VIEW ════════════════════ */}
        {view === 'result' && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="max-w-4xl mx-auto px-6 py-12"
          >
            {/* Top bar */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-claude-muted-fg mb-0.5">Prompt generated for</p>
                <p className="text-base text-claude-text font-medium">{appIdea.length > 80 ? appIdea.substring(0, 80) + '...' : appIdea}</p>
              </div>
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-sm text-claude-muted-fg hover:text-claude-text border border-claude-border rounded-lg px-3 py-2 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                New prompt
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={handleCopy}
                disabled={isGenerating}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  copied
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-claude-primary text-white hover:bg-claude-primary/90'
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied to clipboard' : isGenerating ? 'Generating...' : 'Copy prompt'}
              </button>
              <button
                onClick={handleDownloadTxt}
                disabled={isDownloading || isGenerating}
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm bg-claude-text text-white hover:bg-claude-text/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {isDownloading ? 'Downloading...' : 'Download .txt'}
              </button>
            </div>

            {/* Prompt preview */}
            <div className="bg-white border border-claude-border rounded-2xl overflow-hidden" ref={promptRef}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-claude-border bg-claude-bg/50">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-claude-primary" />
                  <span className="text-sm font-medium text-claude-text">Claude Code Prompt</span>
                  {isGenerating && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-claude-primary">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      streaming
                    </span>
                  )}
                </div>
                <span className="text-xs text-claude-muted-fg">{generatedPRD.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
              </div>
              <div className="p-6 max-h-[600px] overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-claude-text leading-relaxed font-casual break-words">
                  {generatedPRD}
                  {isGenerating && <span className="inline-block w-2 h-4 ml-0.5 bg-claude-primary animate-pulse align-middle" />}
                </pre>
              </div>
            </div>

            {/* Bottom actions */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-claude-border">
              <p className="text-xs text-claude-muted-fg">Paste this prompt directly into Claude Code to build your app</p>
              <button
                onClick={handleCopy}
                className="text-sm text-claude-primary hover:text-claude-primary/80 font-medium transition-colors"
              >
                {copied ? 'Copied' : 'Copy again'}
              </button>
            </div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* ════════════════════ MODALS ════════════════════ */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentInitiated={() => {
          bufferState();
          localStorage.setItem('claude_landing_autogenerate', '1');
          localStorage.setItem('claude_landing_return', '1');
        }}
        context="prd-starter"
      />
      {showAuthModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-6 z-50 overflow-y-auto"
          onClick={() => setShowAuthModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg my-4 sm:my-8 shadow-2xl overflow-hidden max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-y-auto flex-1">
              <Auth onClose={() => setShowAuthModal(false)} defaultMode="signup" variant="modal" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaudeCodeLanding;
